import React, { useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  IconButton,
  Tooltip,
  Chip,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { DataGrid, type GridColDef } from '@mui/x-data-grid';
import { useFormulaStore } from '../store/formulaStore';
import EditBaseCalculo from './EditBaseCalculo';

const BasesCalculoPage: React.FC = () => {
  const {
    basesCalculo,
    loading,
    dialogOpen,
    fetchBasesCalculo,
    deleteBaseCalculo,
    openDialog,
    closeDialog,
  } = useFormulaStore();

  useEffect(() => {
    fetchBasesCalculo();
  }, [fetchBasesCalculo]);

  const handleDelete = async (id: number) => {
    if (window.confirm('¿Está seguro de eliminar esta base de cálculo?')) {
      await deleteBaseCalculo(id);
    }
  };

  const columns: GridColDef[] = [
    { field: 'clave', headerName: 'Clave', width: 180 },
    { field: 'nombre', headerName: 'Nombre', width: 220 },
    {
      field: 'formula',
      headerName: 'Fórmula',
      flex: 1,
      minWidth: 300,
      renderCell: (params) => (
        <Typography
          variant="body2"
          sx={{ fontFamily: 'Consolas, Monaco, monospace', fontSize: '0.8rem' }}
        >
          {params.value}
        </Typography>
      ),
    },
    {
      field: 'esBaseImponible',
      headerName: 'Base Imponible',
      width: 120,
      renderCell: (params) => (
        <Chip
          label={params.value ? 'Sí' : 'No'}
          size="small"
          color={params.value ? 'primary' : 'default'}
          variant="outlined"
        />
      ),
    },
    {
      field: 'activo',
      headerName: 'Estado',
      width: 90,
      renderCell: (params) => (
        <Chip
          label={params.value ? 'Activo' : 'Inactivo'}
          size="small"
          color={params.value ? 'success' : 'default'}
          variant="outlined"
        />
      ),
    },
    { field: 'orden', headerName: 'Orden', width: 70, type: 'number' },
    {
      field: 'actions',
      headerName: 'Acciones',
      width: 100,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Box>
          <Tooltip title="Editar">
            <IconButton size="small" onClick={() => openDialog(params.row)}>
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Eliminar">
            <IconButton
              size="small"
              color="error"
              onClick={() => handleDelete(params.row.idBaseCalculo)}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h5" fontWeight="bold">
            Bases de Cálculo
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Fórmulas reutilizables para el cálculo de planilla (bases imponibles, tarifas, etc.)
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => openDialog()}>
          Nueva Base
        </Button>
      </Box>

      <Paper sx={{ height: 'calc(100vh - 220px)' }}>
        <DataGrid
          rows={basesCalculo}
          columns={columns}
          getRowId={(row) => row.idBaseCalculo}
          loading={loading}
          pageSizeOptions={[10, 25, 50]}
          initialState={{
            pagination: { paginationModel: { pageSize: 10 } },
          }}
          disableRowSelectionOnClick
          sx={{
            border: 'none',
            '& .MuiDataGrid-columnHeaders': {
              backgroundColor: '#f8fafc',
            },
          }}
        />
      </Paper>

      <EditBaseCalculo
        open={dialogOpen}
        onClose={closeDialog}
      />
    </Box>
  );
};

export default BasesCalculoPage;
